import { Box, Grid, Tabs } from '@mui/material';
import { DrawerHeader } from '../../../../../../components/Drawer/DrawerHeader';
import BackdropComponent from '../../../../../../components/Backdrop/BackdropComponent';
import { useState } from 'react';
import RadiologicalEquipmentForm from '../forms/RadiologicalEquipmentForm';
import DosimetryForm from '../forms/DosimetryForm';
import { useAppSelector } from '../../../../../../hooks/reduxHooks';
import ResponsibleForm from '../forms/ResponsibleForm';
import useRadiologicalEquipmentForm from '../../hooks/useRadiologicalEquipmentForm';
import { useDosimetryForm } from '../../hooks/useDosimetryForm';
import { useResponsiblesForm } from '../../hooks/useResponsiblesForm';
import { enqueueSnackbar } from 'notistack';
import {
    useCreateRadiologicalEquipmentMutation,
    useDeleteCarcinogenicEquipmentResponsibleMutation,
    useEditCarcinogenicEquipmentMutation
} from '../../../../../../services/carcinogenicApi';
import useAlerts from '../../hooks/useAlerts';
import { EQUIPMENT_TAB_INDEX } from '.';
import { EquipmentTab } from './components/EquipmentTab';
import { TabPanel } from '../../../../../../components/TabPanel';

interface ResponsableItem {
    cuit: string;
    nombre: string;
    apellido: string;
    idTipoResponsable: number;
    permiso: string;
    matricula: string;
    fechaExpedicion: Date;
    fechaVencimiento: Date;
    autorizacionIndividual: string;
    licenciaEspecial: number;
    domicilioHabilitacion: string;
    titulo: string;
}

const NewRadiologicalEquipmentDrawer = ({
    translate,
    handleCloseDrawer,
    isEditing,
    selectedRadiologicalEquipment,
    currentTab,
    setCurrentTab,
    handleTabChange,
    handleCancelClick,
    onlyVisual
}: any) => {
    const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | null>(
        isEditing ? selectedRadiologicalEquipment?.id : null
    );

    const [localResponsibles, setLocalResponsibles] = useState<any>([]);

    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );

    const {
        showConfirmCreateEquipmentAlert,
        showConfirmDeleteResponsibleAlert,
        showConfirmEditEquipmentAlert
    } = useAlerts({ translate });

    const [createRadiologicalEquipment, { isLoading: isCreatingRadiologicalEquipment }] =
        useCreateRadiologicalEquipmentMutation();

    const [editRadiologicalEquipment, { isLoading: isEditingRadiologicalEquipment }] =
        useEditCarcinogenicEquipmentMutation();

    const [deleteEquipmentResponsible, { isLoading: isDeletingEquipmentResponsible }] =
        useDeleteCarcinogenicEquipmentResponsibleMutation();

    const radiologicalEquipmentForm = useRadiologicalEquipmentForm({
        selectedCarcinogenicReportId,
        selectedRadiologicalEquipment,
        isEditing,
        handleCloseDrawer
    });
    const dosimetryForm = useDosimetryForm({
        selectedCarcinogenicReportId,
        selectedRadiologicalEquipment,
        isEditing,
        handleCloseDrawer
    });
    const responsibleForm = useResponsiblesForm({
        selectedCarcinogenicReportId,
        selectedRadiologicalEquipment,
        isEditing,
        handleCloseDrawer
    });

    const {
        handleSubmit: onRadiologicalEquipmentFormSubmit,
        validateForm: validateRadiologicalEquipmentForm,
        formData: equipmentFormData,
        radiologicalEquipmentFieldsConfig
    } = radiologicalEquipmentForm;
    const {
        handleSubmit: onDosimetryFormSubmit,
        validateForm: validateDosimetryForm,
        formData: dosimetryFormData,
        dosimetryFieldsConfig
    } = dosimetryForm;
    const { formData: responsibleFormData } = responsibleForm;

    const handleRadiologicalEquipmentFormSubmit = () => {
        if(onlyVisual) {
            setCurrentTab(EQUIPMENT_TAB_INDEX.DOSIMETRY);
            return
        }
        onRadiologicalEquipmentFormSubmit({
            onValidationSuccess: () => setCurrentTab(EQUIPMENT_TAB_INDEX.DOSIMETRY)
        });
    };

    const handleDosimetryFormSubmit = () => {
        if(onlyVisual) {
            setCurrentTab(EQUIPMENT_TAB_INDEX.RESPONSIBLES);
            return
        }
        onDosimetryFormSubmit({
            onValidationSuccess: () => setCurrentTab(EQUIPMENT_TAB_INDEX.RESPONSIBLES)
        });
    };

    const handleDeleteLocalEquipment = (localResponsibleIndex: number) => {
        const newResponsiblesList = [...localResponsibles];
        newResponsiblesList.splice(localResponsibleIndex, 1);
        setLocalResponsibles(newResponsiblesList);
    };

    const handleDeleteLocalResponsibleClick = (localResponsibleIndex: number) => {
        showConfirmDeleteResponsibleAlert({
            confirmAction: () => handleDeleteLocalEquipment(localResponsibleIndex)
        });
    };

    const handleDeleteSaveResponsibleClick = (responsibleId: number) => {
        showConfirmDeleteResponsibleAlert({
            confirmAction: () => handleDeleteSavedEquipment(responsibleId)
        });
    };

    const handleDeleteSavedEquipment = async (responsibleId: number) => {
        try {
            if (!selectedCarcinogenicReportId || !selectedEquipmentId) return;
            const response: any = await deleteEquipmentResponsible({
                reportId: selectedCarcinogenicReportId,
                substanceId: selectedRadiologicalEquipment?.sustancia?.id || 0,
                equipmentId: selectedEquipmentId,
                responsibleId: responsibleId
            });
            if (response?.error) {
                enqueueSnackbar(translate('enqueues.deleteResponsible.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(translate('enqueues.deleteResponsible.success'), {
                    variant: 'success'
                });
            }
        } catch (error) {
            enqueueSnackbar(translate('enqueues.common.somethingWentWrong'), { variant: 'error' });
        }
    };

    const handleAddNewResponsible = () => {
        const adaptedResponsable = {
            cuit: responsibleFormData?.cuit,
            nombre: responsibleFormData?.responsibleName,
            apellido: responsibleFormData?.responsibleSurname,
            idTipoResponsable: responsibleFormData?.responsibleType,
            permiso: responsibleFormData?.permission,
            matricula: responsibleFormData?.professionalLicense,
            fechaExpedicion: responsibleFormData?.expeditionDate,
            fechaVencimiento: responsibleFormData?.expirationDate,
            autorizacionIndividual: responsibleFormData?.individualAuthorization,
            licenciaEspecial: responsibleFormData?.specialLicense?.id,
            domicilioHabilitacion: responsibleFormData?.habilitationAddress,
            titulo: responsibleFormData?.professionalDegree
        };
        const newResponsiblesList = [...localResponsibles, adaptedResponsable];

        setLocalResponsibles(newResponsiblesList);
    };

    const handleFullSubmitClick = () => {
        if (isEditing) {
            showConfirmEditEquipmentAlert({ confirmAction: handleFullSubmit });
        } else {
            showConfirmCreateEquipmentAlert({ confirmAction: handleFullSubmit });
        }
    };

    const handleFullSubmit = async () => {
        if (
            !validateRadiologicalEquipmentForm(equipmentFormData, radiologicalEquipmentFieldsConfig)
        ) {
            enqueueSnackbar('Por favor revise los datos de Equipo', { variant: 'error' });
            return;
        }

        if (!validateDosimetryForm(dosimetryFormData, dosimetryFieldsConfig)) {
            enqueueSnackbar('Por favor revise los datos de Disometría', { variant: 'error' });
            return;
        }

        if (
            localResponsibles.length === 0 &&
            selectedRadiologicalEquipment?.responsables?.length === 0
        ) {
            enqueueSnackbar('Debe haber al menos 1 responable', { variant: 'error' });
            return;
        }

        if (!selectedCarcinogenicReportId) return;

        try {
            const adaptedEquipment = {
                equipo: {
                    marca: equipmentFormData.brand,
                    modelo: equipmentFormData.model,
                    idTipoEquipo: equipmentFormData.equipmentType,
                    idCaracteristica: equipmentFormData.characteristics,
                    idTipoRadiacion: equipmentFormData.radiationType,
                    actividad: equipmentFormData.activityCURIE,
                    radioisotopoFuente: equipmentFormData.radioisotopeOfTheSource,
                    idTipoFuente: equipmentFormData.sourceType,
                    potenciaKVP: Number(equipmentFormData.equipmentPowerKVP),
                    potenciaMAP: Number(equipmentFormData.equipmentPowerMAP),
                    potenciaMEV: Number(equipmentFormData.equipmentPowerMEV),
                    procesamientoImagenes: equipmentFormData.imageProcessing,
                    idMetodoProcesamiento: equipmentFormData.processingMethod,
                    observaciones: equipmentFormData.observations,
                    dosimetria: {
                        cuit: dosimetryFormData.cuit,
                        tipo: dosimetryFormData.dosimetryType,
                        metodo: dosimetryFormData.dosimetryMethod,
                        cantidadPersonal: Number(dosimetryFormData.personalQuantity)
                    },
                    responsables: localResponsibles
                }
            };

            if (isEditing && selectedEquipmentId) {
                const response: any = await editRadiologicalEquipment({
                    reportId: selectedCarcinogenicReportId,
                    substanceId: equipmentFormData.substance,
                    equipmentId: selectedEquipmentId,
                    body: adaptedEquipment
                });
                if (response?.error) {
                    enqueueSnackbar(translate('enqueues.editEquipment.error'), {
                        variant: 'error'
                    });
                } else {
                    enqueueSnackbar(translate('enqueues.editEquipment.success'), {
                        variant: 'success'
                    });
                    handleCloseDrawer();
                }
                return;
            }

            const response: any = await createRadiologicalEquipment({
                reportId: selectedCarcinogenicReportId,
                substanceId: equipmentFormData.substance,
                body: adaptedEquipment
            });
            if (response?.error) {
                enqueueSnackbar(translate('enqueues.createEquipment.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(translate('enqueues.createEquipment.success'), {
                    variant: 'success'
                });
                handleCloseDrawer();
            }
        } catch (error) {
            enqueueSnackbar(translate('enqueues.common.somethingWentWrong'), { variant: 'error' });
        }
    };

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            padding={1}
            sx={{ flexDirection: 'column', height: '100%' }}
        >
            <Grid display="flex" padding={1} sx={{ flexDirection: 'column', height: '100%' }}>
                <DrawerHeader
                    text={translate(
                        `radiologicalEquipmentForm.titles.${isEditing ? 'edit' : 'new'}`
                    )}
                    onIconClick={handleCancelClick}
                />
                <Grid display="flex" direction={'row'} bgcolor={'#red'} pb={2} mt={4}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        sx={{ display: 'flex', width: '100%' }}
                    >
                        <EquipmentTab
                            label={translate('radiologicalEquipmentForm.tabs.equipment')}
                            value={EQUIPMENT_TAB_INDEX.EQUIPMENT}
                        />
                        <EquipmentTab
                            label={translate('radiologicalEquipmentForm.tabs.dosimetry')}
                            value={EQUIPMENT_TAB_INDEX.DOSIMETRY}
                        />
                        <EquipmentTab
                            label={translate('radiologicalEquipmentForm.tabs.responsibles')}
                            value={EQUIPMENT_TAB_INDEX.RESPONSIBLES}
                        />
                    </Tabs>
                </Grid>
                <Grid
                    display="flex"
                    justifyContent="space-between"
                    sx={{ height: '100%' }}
                    flexDirection={'row-reverse'}
                >
                    <TabPanel value={currentTab} index={EQUIPMENT_TAB_INDEX.EQUIPMENT}>
                        <RadiologicalEquipmentForm
                            selectedRadiologicalEquipment={selectedRadiologicalEquipment}
                            isEditing={isEditing}
                            handleCloseDrawer={handleCloseDrawer}
                            radiologicalEquipmentForm={radiologicalEquipmentForm}
                            handleSubmit={handleRadiologicalEquipmentFormSubmit}
                            handleCancelClick={handleCancelClick}
                            onlyVisual={onlyVisual}
                        />
                    </TabPanel>
                    <TabPanel value={currentTab} index={EQUIPMENT_TAB_INDEX.DOSIMETRY}>
                        <DosimetryForm
                            selectedCarcinogenicReportId={selectedCarcinogenicReportId}
                            isEditing={isEditing}
                            handleCloseDrawer={handleCloseDrawer}
                            dosimetryForm={dosimetryForm}
                            handleSubmit={handleDosimetryFormSubmit}
                            handleCancelClick={handleCancelClick}
                            onlyVisual={onlyVisual}
                        />
                    </TabPanel>
                    <TabPanel value={currentTab} index={EQUIPMENT_TAB_INDEX.RESPONSIBLES}>
                        <ResponsibleForm
                            selectedRadiologicalEquipment={selectedRadiologicalEquipment}
                            selectedCarcinogenicReportId={selectedCarcinogenicReportId}
                            isEditing={isEditing}
                            handleCloseDrawer={handleCloseDrawer}
                            responsibleForm={responsibleForm}
                            handleAddNewResponsible={handleAddNewResponsible}
                            localResponsibles={localResponsibles}
                            handleSubmit={handleFullSubmitClick}
                            handleDeleteLocalResponsibleClick={handleDeleteLocalResponsibleClick}
                            handleDeleteSaveResponsibleClick={handleDeleteSaveResponsibleClick}
                            selectedEquipmentSubstance={equipmentFormData?.substance}
                            handleCancelClick={handleCancelClick}
                            onlyVisual={onlyVisual}
                        />
                    </TabPanel>
                </Grid>
            </Grid>
            <BackdropComponent
                loading={
                    isDeletingEquipmentResponsible ||
                    isCreatingRadiologicalEquipment ||
                    isEditingRadiologicalEquipment
                }
            />
        </Box>
    );
};

export default NewRadiologicalEquipmentDrawer;
